import React, { useCallback, useEffect, useState } from 'react';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import { formatPrice } from '../../utils/format';
import Loading from '../../components/Loading';
import Sidebar from '../../components/Sidebar';
import ModalClassificacao from '../../components/Modal/ModalClassificacao';
import ModalGrupoComplemento from '../../components/Modal/ModalGrupoComplemento';
import ModalPausarAtivar from '../../components/Modal/ModalPausarAtivar';
import ModalProduto from '../../components/ComponentsProdutos/ModalProduto';
import DataTableProducts from '../../components/ComponentsProdutos/DataTableProducts';

import { Container, Content, Header, ButtonAddItemContent } from './styles';

type ClassificacaoData = {
  id: number;
  id_sub: number;
  nome: string;
  prod_visible: boolean;
  status: string;
}
type ProdutoData = {
  id: string;
  categoria: string;
  cod_barras: string;
  complementos: string;
  descricao: string;
  nome: string;
  promocao: string;
  qtd: number;
  status: string;
  url_imagem: string;
  id_classif: number;
	valor: number;
	total: number;
	valorFormatted: string;
	totalFormatted: string;
}
type GrupoData = {
  id: string;
  nome: string;
  obrigatorio: string;
  min_itens: number;
  max_itens: number;
  complementos: ComplementosData[];
}
type ComplementosData = {
  cod: string;
  nome: string;
  descricao: string;
  valor: number;
  qtd_gratis: number;
  qtd_maxima: number;
}

const Produtos: React.FC = () => {
  const { company, signOut, permissions } = useAuth();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [classificacoes, setClassificacoes] = useState<ClassificacaoData[]>([]);
  const [produtos, setProdutos] = useState<ProdutoData[]>([]);

  const [modalClassificacaoVisible, setModalClassificacaoVisible] = useState(
    false,
  );
  const [modalGrupoCompVisible, setModalGrupoCompVisible] = useState(false);
  const [modalProdutoVisible, setModalProdutoVisible] = useState(false);
  const [modalPausarAtivarVisible, setModalPausarAtivarVisible] = useState(
    false,
  );
  const [classifModal, setClassifModal] = useState<ClassificacaoData>(
    {} as ClassificacaoData,
  );
  const [produtoModal, setProdutoModal] = useState<ProdutoData>(
    {} as ProdutoData,
  );
  const [grupoModal, setGrupoModal] = useState<GrupoData>({} as GrupoData);

  useEffect(() => {
    setLoading(true);
    api
      .get(`portal/cardapio/index`)
      .then(response => {
				if (response.data.produtos.length > 0) {
					const auxProducts = response.data.produtos.map((product: ProdutoData) => {
						return {
							...product,
							valorFormatted: formatPrice(product.valor),
							totalFormatted: formatPrice(product.total)
						}
					});
					setProdutos(auxProducts);
				}

        setClassificacoes(response.data.classificacoes);
      })
      .catch(() => {
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [company.id, signOut, addToast]);

  // UseEffect para bloquear a rolagem ao abrir algum modal
  useEffect(() => {
    if (
      modalClassificacaoVisible ||
      modalGrupoCompVisible ||
      modalProdutoVisible ||
      modalPausarAtivarVisible
    ) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [
    modalClassificacaoVisible,
    modalGrupoCompVisible,
    modalProdutoVisible,
    modalPausarAtivarVisible,
  ]);

  const handleAtivarPausarClassif = useCallback(
    async (status: string, classif: ClassificacaoData) => {
      await api
        .post('portal/classif/status', {
          emp: company.id,
          id_class: classif.id,
          id_sub: classif.id_sub,
          status,
        })
        .then(() => {
          const auxClassificacoes = classificacoes;
          const indexItem = auxClassificacoes.findIndex(
            item => item.id === classif.id,
          );
          auxClassificacoes[indexItem].status = status;
          classificacoes[indexItem].status = status;

          addToast({
            type: 'info',
            title: 'Status Classifição Alterada!',
            description: `Status da Classificação alterada para ${
              status === 'S' ? 'ATIVO' : 'PAUSADO'
            }`,
          });
        })
        .catch(err => {
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.signin
          ) {
            signOut();
            addToast({
              type: 'info',
              title: 'Login',
              description:
                'Seu login expirou, faça o login novamente para a sua segurança.',
            });
          }
        });
    },
    [company.id, classificacoes, signOut, addToast],
  );

  const handleAtivarPausarProduto = useCallback(
    async (status: string, id: string) => {
      await api
        .post('portal/produto/status', {
          emp: company.id,
          id,
          status,
        })
        .then(() => {
          const auxProdutos = produtos;
          const indexItem = auxProdutos.findIndex(item => item.id === id);
          auxProdutos[indexItem].status = status;
          produtos[indexItem].status = status;

          setProdutos(auxProdutos);

          addToast({
            type: 'success',
            title: 'Status Produto Alterado!',
            description: `Status do Produto alterado para ${
              status === 'S' ? 'ATIVO' : 'PAUSADO'
            }`,
          });
        })
        .catch(err => {
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.signin
          ) {
            signOut();
            addToast({
              type: 'info',
              title: 'Login',
              description:
                'Seu login expirou, faça o login novamente para a sua segurança.',
            });
          }
        });
    },
    [company.id, produtos, signOut, addToast],
  );

 function handleFindClassif(classif: string) {
		const classifIndex = classificacoes.findIndex(classiff => classiff.nome === classif);
		setClassifModal(classifIndex !== -1 ? classificacoes[classifIndex] : {} as ClassificacaoData);
	}

  return (
    <>
      {modalProdutoVisible && (
        <ModalProduto
          isVisible={modalProdutoVisible}
          onClose={() => setModalProdutoVisible(false)}
          produtos={produtos}
          product={produtoModal}
          setModalGrupoCompVisible={setModalGrupoCompVisible}
          setGrupoModal={setGrupoModal}
          classificacao={classifModal}
        />
      )}

			{modalGrupoCompVisible && (
				<ModalGrupoComplemento
					isVisible={modalGrupoCompVisible}
					onClose={() => setModalGrupoCompVisible(false)}
					grupo={grupoModal}
					produtos={produtos}
				/>
			)}

      {modalPausarAtivarVisible && (
        <ModalPausarAtivar
          classificacao={classifModal}
          handleAtivarPausarClassif={handleAtivarPausarClassif}
          produto={produtoModal}
          handleAtivarPausarProduto={handleAtivarPausarProduto}
          onClose={() => setModalPausarAtivarVisible(false)}
        />
      )}

      <Container>
				<Sidebar />

        {loading ? (
          <Loading />
        ) : (
					<Content>
						<Header>
							<div className='header-texts'>
								<h1>Produtos.</h1>
								<h2>Cadastre ou altere seus produtos.</h2>
							</div>

							{permissions.incluir && (
								<ButtonAddItemContent
									onClick={() => {
										// setClassifModal(classif);
										// setClassifModal(classificacoes[0]);
										handleFindClassif('');
										setProdutoModal({} as ProdutoData);
										setModalProdutoVisible(true);
									}}
								>
									+ Novo
								</ButtonAddItemContent>
							)}
						</Header>

						{produtos.length > 0 && (
							<DataTableProducts
								data={produtos}
								setClassifModal={setClassifModal}
								setProdutoModal={setProdutoModal}
								setModalPausarAtivarVisible={setModalPausarAtivarVisible}
								setModalProdutoVisible={setModalProdutoVisible}
								handleFindClassif={handleFindClassif}
							/>
						)}
					</Content>
        )}

        {modalClassificacaoVisible && (
          <ModalClassificacao
            isVisible={modalClassificacaoVisible}
            onClose={() => setModalClassificacaoVisible(false)}
            classificacao={classifModal}
          />
        )}
      </Container>
    </>
  );
};

export default Produtos;
